import {
  type WorkItem,
  type WorkItemCompletion,
} from '@/features/jobs/bookkeeping/BookkeepingProgressionInfo'
import { isNil } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import type { Id } from '@/infra/id'
import { required, validationContext } from 'validx'

/**
 * A work item's completed count being corrected for a bookkeeping cycle.
 */
export class CorrectWorkItemViewModel {
  /**
   * The ID of the work item being corrected.
   */
  readonly workItemId: Id

  /**
   * The name of the work item's inventory item.
   */
  readonly name: string

  /**
   * The description of the work item's inventory item.
   */
  readonly description: string

  /**
   * How many of the work item the cycle had completed before the correction.
   */
  readonly completedCount: number

  /**
   * The highest count the correction may set: the remaining items plus the completed ones.
   */
  readonly maxCorrectAmount: number

  /**
   * The corrected completed count.
   */
  @observable accessor correctAmount = 0

  /**
   * The bounds of the corrected count.
   */
  readonly fieldLengths = {
    correctAmount: { min: 0 },
  } as const

  /**
   * Validation context.
   */
  readonly validation = validationContext(this, {
    correctAmount: [
      required(),
      () =>
        this.correctedItemsAreValid ||
        `The number of corrected items must be between ${this.fieldLengths.correctAmount.min} and ${this.maxCorrectAmount}.`,
    ],
  })

  constructor(workItem: WorkItem, workItemCompletion?: WorkItemCompletion) {
    const completedCount = workItemCompletion?.completedCount ?? 0

    this.workItemId = workItem.workItemId
    this.name = workItem.inventoryItemName
    this.completedCount = completedCount
    this.correctAmount = completedCount
    this.description = workItem.inventoryItemDescription
    this.maxCorrectAmount = workItem.remaining + completedCount
  }

  /**
   * Whether the corrected items quantity is valid.
   */
  @computed
  get correctedItemsAreValid() {
    return (
      this.correctAmount <= this.maxCorrectAmount &&
      this.correctAmount >= this.fieldLengths.correctAmount.min
    )
  }

  /**
   * Resets the fields.
   */
  @action.bound
  reset() {
    this.correctAmount = this.completedCount
  }

  /**
   * Sets the number of completed items.
   */
  @action.bound
  setCorrectedItems(value: number | null) {
    if (!isNil(value)) {
      this.correctAmount = value
    }
  }
}
